import type { AiCompanyArticle } from '../types/news.ts'
import { selectPublishedArchiveArticles } from './articleArchive.ts'
import { isArticleQualityHeld } from '../../functions/src/articleQualityHold.ts'
import { articleImageCoverage } from '../../functions/src/articleMedia.ts'

export type ArticleDesk = 'ai' | 'robots' | 'cybersecurity'

export function articleDeskFromQuery(query: string): ArticleDesk | null {
  const value = new URLSearchParams(query).get('desk')
  return value === 'ai' || value === 'robots' || value === 'cybersecurity' ? value : null
}

export const articleDeskInfo: Record<ArticleDesk, { label: string; href: string; description: string }> = {
  ai: { label: 'AI', href: '/news', description: 'Models, compute, infrastructure, and the consequences of adoption.' },
  robots: { label: 'Robotics', href: '/news/robots', description: 'Physical AI, machines, and the evidence behind deployment claims.' },
  cybersecurity: { label: 'Cyberdefense', href: '/news/cybersecurity', description: 'Threats, system weaknesses, and the work of building resilience.' },
}

type ArticleTopic = Pick<AiCompanyArticle, 'headline' | 'dek' | 'tags' | 'editorialDesk'>
const editorialDeskForRoute: Record<ArticleDesk, NonNullable<AiCompanyArticle['editorialDesk']>> = {
  ai: 'ai', robots: 'robotics', cybersecurity: 'cyberdefense',
}
const normalize = (value: string) => value.toLocaleLowerCase().replaceAll(/[-‐‑–—]/g, ' ')
const deskPatterns: Record<ArticleDesk, RegExp> = {
  ai: /\b(ai|artificial intelligence|machine learning|language models?|frontier models?|open models?|inference|chatgpt|claude|grok|deepseek|qwen|ai agents?|agentic)\b/,
  robots: /\b(robot(?:s|ics)?|humanoids?|physical ai|embodied ai|vision language action|robot hands|grippers?|tactile sensing|industrial automation|autonomous driving)\b/,
  cybersecurity: /\b(cyber(?:security|defense)?|cyber defense|cyber security|ransomware|malware|phishing|vulnerabilit(?:y|ies)|exploit(?:ed|s|ation)?|patch management|prompt injection|cryptography|ciso|cisa|kev|cloud\s*\/\s*identity|defense\s*\/\s*resilience)\b/,
}

export function articleMatchesDesk(article: ArticleTopic, desk: ArticleDesk): boolean {
  if (article.editorialDesk) return article.editorialDesk === editorialDeskForRoute[desk]
  return deskPatterns[desk].test(normalize([article.headline, article.dek, ...(article.tags ?? [])].join(' ')))
}

export function isOriginalAnalysisArticle(article: ArticleTopic): boolean {
  if ((article.tags ?? []).some((tag) => /^(omikina update|product update)$/i.test(tag.trim()))) return false
  return !/^(?:introducing omikina\b|omikina (?:launches|introduces|now gives)\b|i rebuilt omikina\b|i used ai to build a newsroom\b)/i.test(article.headline.trim())
}

export function selectDeskArticles(articles: AiCompanyArticle[], desk: ArticleDesk, { limit = 3, now = Date.now() }: { limit?: number; now?: number } = {}): AiCompanyArticle[] {
  // A legacy pin-eligibility flag is not an editorial quality or publication decision.
  // The feature uses the publication record, topic relevance, and a visible review disclosure.
  return selectPublishedArchiveArticles(articles, now)
    .filter((article) => isOriginalAnalysisArticle(article) && articleMatchesDesk(article, desk))
    .slice(0, Math.max(0, Math.floor(limit)))
}

const coreCoveragePattern = /\b(?:artificial intelligence|ai|agents?|models?|robot(?:s|ics)?|humanoids?|cyber(?:security|defense)?|data[ -]centers?|compute|chips?|semiconductors?|electricity|power|grids?|nuclear|infrastructure)\b/i
const infrastructurePattern = /\b(?:data[ -]centers?|compute|chips?|semiconductors?|electricity|power|grids?|nuclear|infrastructure)\b/i

export const HOME_ARTICLE_LIMIT = 12

export function selectHomeArticles(articles: AiCompanyArticle[], { limit = HOME_ARTICLE_LIMIT, now = Date.now() }: { limit?: number; now?: number } = {}): AiCompanyArticle[] {
  const relevant = selectPublishedArchiveArticles(articles, now)
    .filter((article) => !isArticleQualityHeld(article.id) && isOriginalAnalysisArticle(article)
      && !['pending', 'blocked', 'requesting', 'uncertain', 'failed'].includes(article.imageStatus ?? '')
      && articleImageCoverage(article) !== 'custom-required'
      && coreCoveragePattern.test(`${article.headline} ${article.dek}`))
  const lead = relevant.find((article) => infrastructurePattern.test(`${article.headline} ${article.dek}`)
    && article.sources.length >= 3
    && (article.sections ?? []).flatMap((section) => section.paragraphs).join(' ').trim().split(/\s+/).length >= 500)
  return (lead ? [lead, ...relevant.filter((article) => article.id !== lead.id)] : relevant)
    .slice(0, Math.max(0, Math.floor(limit)))
}
